import React, { useState } from 'react';
import styles from './profile.module.css';
import { useNavigate } from 'react-router-dom';
import { Typography, Card, Badge, Button, Spinner } from 'glintly-ui';
import { useCancelOrder } from '../../api';
import { toast } from 'react-toastify';
import { Z_INDEX } from '../../constants/zIndex';

const OrdersDetails = ({ fetchUserOrders, ordersLoading, ordersError, orders, user }) => {
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);

  // Cancel order mutation
  const cancelOrderMutation = useCancelOrder();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'delivered':
        return 'success';
      default:
        return 'secondary';
    }
  };

  // Get status display text
  const getStatusText = (order) => {
    if (order.isCancelled || order.orderStatus?.toLowerCase() === 'cancelled') {
      return 'CANCELLED';
    }
    return order.orderStatus || 'PENDING';
  };

  // Get payment status color
  const getPaymentStatusColor = (status) => {
    // error, gray, success, secondary, warning, info
    switch (status?.toLowerCase()) {
      case 'success':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'secondary';
    }
  };

  // Handle order details view
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  // Handle cancel order
  const handleCancelOrder = async (orderId) => {
    console.log('handleCancelOrder called with orderId:', orderId, 'type:', typeof orderId);

    if (
      !window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')
    ) {
      return;
    }

    // Ensure orderId is a string
    const orderIdString = typeof orderId === 'object' ? orderId._id || orderId.id : orderId;
    console.log('Processed orderId:', orderIdString, 'type:', typeof orderIdString);

    setCancellingOrderId(orderIdString);

    try {
      const updatedBy = user?.name || user?.email || 'user';
      await cancelOrderMutation.mutateAsync({ orderId: orderIdString, updatedBy });
      toast.success('Order cancelled successfully!');
      // Refresh orders list
      fetchUserOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel order. Please try again.');
    } finally {
      setCancellingOrderId(null);
    }
  };

  // Check if order can be cancelled
  const canCancelOrder = (order) => {
    const cancellableStatuses = ['PENDING', 'CONFIRMED'];
    return cancellableStatuses.includes(order.orderStatus?.toUpperCase());
  };

  return (
    <div className={styles.ordersSection}>
      <div className={styles.sectionHeader}>
        <Typography variant="h4" style={{ color: '#1e293b', margin: 0 }}>
          My Orders
        </Typography>
        <Button
          variant="ghost"
          onClick={fetchUserOrders}
          disabled={ordersLoading}
          style={{ minWidth: '100px' }}
        >
          {ordersLoading ? <Spinner size="small" /> : 'Refresh'}
        </Button>
      </div>

      {ordersError && (
        <Card
          style={{
            padding: '20px',
            marginBottom: '20px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
          }}
        >
          <Typography variant="body1" style={{ color: '#dc2626', textAlign: 'center' }}>
            {ordersError}
          </Typography>
        </Card>
      )}

      {ordersLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <Spinner size="large" />
          <Typography variant="body1" style={{ marginLeft: '16px' }}>
            Loading your orders...
          </Typography>
        </div>
      ) : orders.length === 0 ? (
        <Card style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
          <Typography variant="h5" style={{ color: '#1e293b', marginBottom: '8px' }}>
            No orders yet
          </Typography>
          <Typography variant="body1" style={{ color: '#64748b', marginBottom: '24px' }}>
            You haven't placed any orders yet. Start shopping to see your orders here.
          </Typography>
          <Button
            variant="primary"
            onClick={() => navigate('/services')}
            style={{ minWidth: '150px' }}
          >
            Start Shopping
          </Button>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {orders.map((order) => (
            <Card key={order._id} style={{ padding: '20px', border: '1px solid #e2e8f0' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px',
                }}
              >
                <div>
                  <Typography variant="h6" style={{ color: '#1e293b', marginBottom: '4px' }}>
                    Order #{order.orderNumber || order._id?.slice(-8).toUpperCase() || 'N/A'}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#64748b' }}>
                    Placed on {formatDate(order.createdAt || order.date)}
                  </Typography>
                  {order.isCancelled && order.cancelledAt && (
                    <Typography variant="caption" style={{ color: '#dc2626', fontSize: '12px' }}>
                      Cancelled on {formatDate(order.cancelledAt)}
                    </Typography>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '8px',
                  }}
                >
                  <Badge variant={getStatusColor(getStatusText(order))}>
                    {getStatusText(order)}
                  </Badge>
                  <Badge variant={getPaymentStatusColor(order.paymentStatus)}>
                    {order.paymentStatus || 'PENDING'}
                  </Badge>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <Typography variant="h6" style={{ color: '#1e293b', marginBottom: '12px' }}>
                  Services ({order.orderItem?.length || 0})
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {order.orderItem?.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div
                        style={{
                          width: '60px',
                          height: '60px',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px',
                          fontSize: '24px',
                        }}
                      >
                        ✂️
                      </div>
                      <div style={{ flex: 1 }}>
                        <Typography
                          variant="body1"
                          style={{ fontWeight: '500', marginBottom: '4px' }}
                        >
                          {item.service?.name || 'Service'}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ color: '#64748b', marginBottom: '4px' }}
                        >
                          Quantity: {item.service?.quantity || 1}
                        </Typography>
                        {item.addOn && item.addOn.length > 0 && (
                          <div style={{ marginTop: '4px' }}>
                            <Badge variant="secondary" style={{ fontSize: '12px' }}>
                              Add-ons: {item.addOn.map((addon) => addon.name).join(', ')}
                            </Badge>
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <Typography variant="body1" style={{ fontWeight: '500' }}>
                          {formatCurrency(item.service?.price || 0)}
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: '#f8fafc',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '16px',
                }}
              >
                <Typography variant="h6" style={{ color: '#1e293b', marginBottom: '12px' }}>
                  Order Summary
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" style={{ color: '#64748b' }}>
                      Service Amount:
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: '500' }}>
                      {formatCurrency(order.paymentDetails?.serviceAmount || order.itemsTotal || 0)}
                    </Typography>
                  </div>
                  {order.paymentDetails?.discountAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" style={{ color: '#64748b' }}>
                        Discount ({order.couponCode || 'N/A'}):
                      </Typography>
                      <Typography variant="body2" style={{ color: '#059669', fontWeight: '500' }}>
                        -{formatCurrency(order.paymentDetails?.discountAmount)}
                      </Typography>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" style={{ color: '#64748b' }}>
                      Subtotal:
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: '500' }}>
                      {formatCurrency(order.paymentDetails?.totalAmount || order.itemsTotal || 0)}
                    </Typography>
                  </div>
                  {order.paymentDetails?.gstAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" style={{ color: '#64748b' }}>
                        GST:
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: '500' }}>
                        {formatCurrency(order.paymentDetails?.gstAmount)}
                      </Typography>
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: '8px',
                      borderTop: '1px solid #e2e8f0',
                      marginTop: '8px',
                    }}
                  >
                    <Typography variant="body1" style={{ fontWeight: '600', color: '#1e293b' }}>
                      Total:
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: '600', color: '#1e293b' }}>
                      {formatCurrency(
                        order.grandTotal || order.paymentDetails?.totalAmountWithGst || 0
                      )}
                    </Typography>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '16px',
                  borderTop: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button
                    variant="ghost"
                    onClick={() => handleViewDetails(order)}
                    style={{ minWidth: '120px' }}
                  >
                    View Details
                  </Button>
                  {canCancelOrder(order) && (
                    <button
                      variant="danger"
                      style={{ minWidth: '120px' }}
                      onClick={() => handleCancelOrder(order._id)}
                      disabled={cancellingOrderId === order._id}
                    >
                      {cancellingOrderId === order._id ? (
                        <>
                          <Spinner size="small" style={{ marginRight: '8px' }} />
                          Cancelling...
                        </>
                      ) : (
                        'Cancel Order'
                      )}
                    </button>
                  )}
                  {(order.orderStatus?.toLowerCase() === 'completed' ||
                    order.orderStatus?.toLowerCase() === 'delivered') && (
                    <Button style={{ minWidth: '120px' }}>Reorder</Button>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {order.paymentStatus?.toLowerCase() === 'success' && (
                    <Badge variant="success" style={{ fontSize: '12px' }}>
                      ✅ Paid
                    </Badge>
                  )}
                  <Typography variant="caption" style={{ color: '#9ca3af' }}>
                    Order ID: {order._id?.slice(-8).toUpperCase()}
                  </Typography>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: Z_INDEX.modal,
            padding: '20px',
          }}
        >
          <Card
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              position: 'relative',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <Typography variant="h5" style={{ color: '#1e293b' }}>
                Order Details
              </Typography>
              <Button variant="ghost" onClick={closeModal}>
                ✕
              </Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <Typography variant="h6" style={{ color: '#1e293b', marginBottom: '8px' }}>
                  Order #{selectedOrder.orderNumber || selectedOrder._id?.slice(-8).toUpperCase()}
                </Typography>
                <Typography variant="body2" style={{ color: '#64748b' }}>
                  Placed on {formatDate(selectedOrder.createdAt || selectedOrder.date)}
                </Typography>
              </div>

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Badge variant={getStatusColor(getStatusText(selectedOrder))}>
                    {getStatusText(selectedOrder)}
                  </Badge>
                  <Badge variant={getPaymentStatusColor(selectedOrder.paymentStatus)}>
                    {selectedOrder.paymentStatus || 'PENDING'}
                  </Badge>
                </div>
                {canCancelOrder(selectedOrder) && (
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => {
                      closeModal();
                      handleCancelOrder(selectedOrder._id);
                    }}
                    disabled={cancellingOrderId === selectedOrder._id}
                  >
                    {cancellingOrderId === selectedOrder._id ? (
                      <>
                        <Spinner size="small" style={{ marginRight: '8px' }} />
                        Cancelling...
                      </>
                    ) : (
                      'Cancel Order'
                    )}
                  </Button>
                )}
              </div>

              <div>
                <Typography variant="h6" style={{ color: '#1e293b', marginBottom: '12px' }}>
                  Services
                </Typography>
                {selectedOrder.orderItem?.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <div>
                      <Typography variant="body1" style={{ fontWeight: '500' }}>
                        {item.service?.name || 'Service'}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#64748b' }}>
                        Quantity: {item.service?.quantity || 1}
                      </Typography>
                    </div>
                    <Typography variant="body1" style={{ fontWeight: '500' }}>
                      {formatCurrency(item.service?.price || 0)}
                    </Typography>
                  </div>
                ))}
              </div>

              <div
                style={{
                  backgroundColor: '#f8fafc',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Typography variant="h6" style={{ color: '#1e293b', marginBottom: '12px' }}>
                  Order Summary
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" style={{ color: '#64748b' }}>
                      Service Amount:
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: '500' }}>
                      {formatCurrency(
                        selectedOrder.paymentDetails?.serviceAmount || selectedOrder.itemsTotal || 0
                      )}
                    </Typography>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" style={{ color: '#64748b' }}>
                      Subtotal:
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: '500' }}>
                      {formatCurrency(
                        selectedOrder.paymentDetails?.totalAmount || selectedOrder.itemsTotal || 0
                      )}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: '8px',
                      borderTop: '1px solid #e2e8f0',
                      marginTop: '8px',
                    }}
                  >
                    <Typography variant="body1" style={{ fontWeight: '600', color: '#1e293b' }}>
                      Total:
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: '600', color: '#1e293b' }}>
                      {formatCurrency(
                        selectedOrder.grandTotal ||
                          selectedOrder.paymentDetails?.totalAmountWithGst ||
                          0
                      )}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default OrdersDetails;
